import * as React from 'react';
import { CalendarIcon, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import BottomSheet from '@/components/BottomSheet';
import {
  parseDateTimePickerValue,
  formatDateTimePickerDisplay,
  buildDateTimeFromParts,
  isPastDateTime,
  isDateOnlyValue,
  parseNaturalLanguageDateTime,
  getMinutesForStep,
} from '@/lib/datetimepicker-utils';
import { toast } from 'sonner';

interface DateTimePickerProps {
  value: string | null | undefined;
  onChange: (isoValue: string | null) => void;
  className?: string;
  placeholder?: string;
}

type MinuteStep = 15 | 30;

const HOURS = Array.from({ length: 24 }, (_, i) => i);

// Standard-Uhrzeit, wenn noch keine echte Uhrzeit gesetzt ist (statt 00:00).
const DEFAULT_HOUR = 9;
const DEFAULT_MINUTE = 0;

/**
 * Liefert die Start-Werte für den Picker. Reine Datumswerte (ohne Uhrzeit)
 * würden sonst auf 00:00 stehen — hier setzen wir stattdessen eine sinnvolle
 * Default-Uhrzeit, damit der Nutzer nicht bei Mitternacht festhängt.
 */
function getDraftParts(value: string | null | undefined): {
  date: Date | undefined;
  hour: number;
  minute: number;
} {
  const p = parseDateTimePickerValue(value);
  if (!p) return { date: undefined, hour: DEFAULT_HOUR, minute: DEFAULT_MINUTE };
  if (isDateOnlyValue(value)) {
    return { date: p.date, hour: DEFAULT_HOUR, minute: DEFAULT_MINUTE };
  }
  return p;
}

/**
 * Bottom-Sheet nur bei wirklich schmalem Bildschirm (< 768px, z.B. Handy).
 * Auf breiteren Geräten (auch Surface/Touch-Laptop) wird der Popover genutzt —
 * dieser ist auf die verfügbare Höhe begrenzt und scrollt innen, sodass
 * OK/Übernehmen immer erreichbar ist.
 */
function useIsMobile(): boolean {
  const check = React.useCallback(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  }, []);
  const [isMobile, setIsMobile] = React.useState(check);
  React.useEffect(() => {
    function onResize() {
      setIsMobile(check());
    }
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [check]);
  return isMobile;
}

function nextBusinessDay(from: Date): Date {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  d.setDate(d.getDate() + 1);
  while (d.getDay() === 0 || d.getDay() === 6) {
    d.setDate(d.getDate() + 1);
  }
  return d;
}

function addDaysLocal(base: Date, days: number): Date {
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  d.setDate(d.getDate() + days);
  return d;
}

function nextMonday(from: Date): Date {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const day = d.getDay();
  const diff = day === 0 ? 1 : 8 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

const QUICK_PICKS = [
  {
    label: 'Heute 17:00',
    resolve: () => ({ date: addDaysLocal(new Date(), 0), hour: 17, minute: 0 }),
  },
  {
    label: 'Morgen 9:00',
    resolve: () => ({ date: addDaysLocal(new Date(), 1), hour: 9, minute: 0 }),
  },
  {
    label: 'In 3 Tagen',
    resolve: () => ({ date: addDaysLocal(new Date(), 3), hour: 9, minute: 0 }),
  },
  {
    label: 'Nächste Woche',
    resolve: () => ({ date: nextMonday(new Date()), hour: 9, minute: 0 }),
  },
  {
    label: 'Nächster Werktag 9:00',
    resolve: () => ({ date: nextBusinessDay(new Date()), hour: 9, minute: 0 }),
  },
];

export function DateTimePicker({
  value,
  onChange,
  className,
  placeholder = 'Datum & Uhrzeit wählen',
}: DateTimePickerProps) {
  const [open, setOpen] = React.useState(false);
  const isMobile = useIsMobile();

  const parsed = parseDateTimePickerValue(value);
  const initialDraft = getDraftParts(value);
  const [draftDate, setDraftDate] = React.useState<Date | undefined>(initialDraft.date);
  const [draftHour, setDraftHour] = React.useState<number>(initialDraft.hour);
  const [draftMinute, setDraftMinute] = React.useState<number>(initialDraft.minute);
  const [minuteStep, setMinuteStep] = React.useState<MinuteStep>(15);
  const [textInput, setTextInput] = React.useState('');
  const [textError, setTextError] = React.useState(false);

  const minutes = getMinutesForStep(minuteStep);

  React.useEffect(() => {
    if (!open) return;
    const p = getDraftParts(value);
    setDraftDate(p.date);
    setDraftHour(p.hour);
    setDraftMinute(p.minute);
    setTextInput('');
    setTextError(false);
  }, [open, value]);

  // Snap draftMinute to nearest allowed step when step changes
  React.useEffect(() => {
    const nearest = minutes.reduce((prev, cur) =>
      Math.abs(cur - draftMinute) < Math.abs(prev - draftMinute) ? cur : prev
    );
    if (nearest !== draftMinute) setDraftMinute(nearest);
  }, [minuteStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // Best practice: jede Auswahl wird SOFORT übernommen (onChange), damit der
  // Nutzer nicht erst "OK" treffen muss. Funktioniert auch, wenn der Footer
  // (z.B. auf dem Surface) gerade nicht im Blick ist.
  const applyNow = React.useCallback(
    (date: Date | undefined, hour: number, minute: number) => {
      if (!date) return;
      onChange(buildDateTimeFromParts(date, hour, minute));
    },
    [onChange]
  );

  function handleDateSelect(day: Date | undefined) {
    setDraftDate(day);
    applyNow(day, draftHour, draftMinute);
  }

  function handleHourChange(h: number) {
    setDraftHour(h);
    applyNow(draftDate, h, draftMinute);
  }

  function handleMinuteChange(m: number) {
    setDraftMinute(m);
    applyNow(draftDate, draftHour, m);
  }

  function handleOk() {
    if (!draftDate) {
      setOpen(false);
      return;
    }
    if (isPastDateTime(draftDate, draftHour, draftMinute)) {
      toast.warning('Fälligkeitsdatum liegt in der Vergangenheit', {
        description: 'Der Termin wurde trotzdem übernommen.',
      });
    }
    onChange(buildDateTimeFromParts(draftDate, draftHour, draftMinute));
    setOpen(false);
  }

  function handleCancel() {
    setOpen(false);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleOk();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  }

  function applyQuickPick(resolve: () => { date: Date; hour: number; minute: number }) {
    const { date, hour, minute } = resolve();
    const nearest = minutes.reduce((prev, cur) =>
      Math.abs(cur - minute) < Math.abs(prev - minute) ? cur : prev
    );
    setDraftDate(date);
    setDraftHour(hour);
    setDraftMinute(nearest);
    // Schnellwahl ist eine fertige Aktion: sofort übernehmen und schließen.
    onChange(buildDateTimeFromParts(date, hour, nearest));
    setOpen(false);
  }

  function handleTextInput(raw: string) {
    setTextInput(raw);
    setTextError(false);
    if (!raw.trim()) return;
    const nlpResult = parseNaturalLanguageDateTime(raw);
    if (nlpResult) {
      const nearest = minutes.reduce((prev, cur) =>
        Math.abs(cur - nlpResult.minute) < Math.abs(prev - nlpResult.minute) ? cur : prev
      );
      setDraftDate(nlpResult.date);
      setDraftHour(nlpResult.hour);
      setDraftMinute(nearest);
      setTextError(false);
      // Eingabe sofort übernehmen, damit sie nicht verloren geht.
      applyNow(nlpResult.date, nlpResult.hour, nearest);
    } else {
      setTextError(true);
    }
  }

  // Anzeige im Trigger: bei reinem Datum (ohne echte Uhrzeit) KEIN "00:00"
  // anzeigen, sondern nur das Datum. Sonst Datum + Uhrzeit.
  const displayLabel =
    parsed && parsed.date
      ? isDateOnlyValue(value)
        ? parsed.date.toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })
        : formatDateTimePickerDisplay(parsed.date, parsed.hour, parsed.minute)
      : null;

  const selectClass =
    'w-full h-9 text-sm px-2 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-1 focus:ring-[#B91C1C]/30 focus:border-[#B91C1C]';

  const pickerContent = (
    <div
      className="flex flex-col min-h-0 w-[300px] max-w-[92vw]"
      style={{ maxHeight: 'var(--radix-popover-content-available-height, 85vh)' }}
      onKeyDown={handleKeyDown}
      data-testid="date-time-picker-popover"
    >
      {/* Scrollbarer Inhaltsbereich — OK/Abbrechen bleiben darunter immer sichtbar */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain" style={{ scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch' }}>
        {/* Text input */}
        <div className="px-3 pt-3 pb-1">
          <input
            type="text"
            value={textInput}
            onChange={(e) => handleTextInput(e.target.value)}
            placeholder='z.B. "morgen 14:30" oder "28.04.2026 09:00"'
            data-testid="date-time-picker-text-input"
            className={cn(
              'w-full text-sm px-2 py-1.5 rounded border bg-white focus:outline-none focus:ring-1',
              textError
                ? 'border-red-400 focus:ring-red-300'
                : 'border-gray-200 focus:ring-[#B91C1C]/30 focus:border-[#B91C1C]'
            )}
          />
          {textError && (
            <p className="text-[10px] text-red-500 mt-0.5">
              Format nicht erkannt
            </p>
          )}
        </div>

        {/* Quick picks */}
        <div className="flex flex-wrap gap-1 px-3 pb-2">
          {QUICK_PICKS.map((qp) => (
            <button
              key={qp.label}
              type="button"
              data-testid={`quick-pick-${qp.label}`}
              onClick={() => applyQuickPick(qp.resolve)}
              className="text-[11px] px-2 py-0.5 rounded-full border border-gray-200 bg-gray-50 hover:bg-[#B91C1C]/10 hover:border-[#B91C1C]/40 text-gray-600 hover:text-[#B91C1C] transition-colors"
            >
              {qp.label}
            </button>
          ))}
        </div>

        <div className="flex justify-center">
          <Calendar
            mode="single"
            selected={draftDate}
            onSelect={handleDateSelect}
            initialFocus
          />
        </div>

        <div className="border-t border-gray-100 p-3">
          {/* Minute step toggle */}
          <div className="flex items-center justify-end gap-1 mb-2">
            <span className="text-[10px] text-gray-400 mr-1">Schrittweite:</span>
            {([15, 30] as MinuteStep[]).map((s) => (
              <button
                key={s}
                type="button"
                data-testid={`minute-step-${s}`}
                onClick={() => setMinuteStep(s)}
                className={cn(
                  'text-[11px] px-2 py-0.5 rounded border transition-colors',
                  minuteStep === s
                    ? 'bg-[#B91C1C] text-white border-[#B91C1C]'
                    : 'border-gray-200 text-gray-500 hover:border-gray-400'
                )}
              >
                {s} Min
              </button>
            ))}
          </div>

          {/* Kompakte Uhrzeit-Auswahl (native Selects statt langer Scroll-Listen) */}
          <div className="flex items-end gap-2">
            <div className="flex flex-col flex-1 min-w-0">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">
                Stunde
              </label>
              <select
                aria-label="Stunde"
                data-testid="date-time-picker-hours"
                value={draftHour}
                onChange={(e) => handleHourChange(Number(e.target.value))}
                className={selectClass}
              >
                {HOURS.map((h) => (
                  <option key={h} value={h}>
                    {h.toString().padStart(2, '0')}
                  </option>
                ))}
              </select>
            </div>

            <span className="pb-2 text-gray-400 font-medium">:</span>

            <div className="flex flex-col flex-1 min-w-0">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">
                Minute
              </label>
              <select
                aria-label="Minute"
                data-testid="date-time-picker-minutes"
                value={draftMinute}
                onChange={(e) => handleMinuteChange(Number(e.target.value))}
                className={selectClass}
              >
                {minutes.map((m) => (
                  <option key={m} value={m}>
                    {m.toString().padStart(2, '0')}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Preview */}
          {draftDate && (
            <p className="text-xs text-gray-500 text-center mt-3">
              {formatDateTimePickerDisplay(draftDate, draftHour, draftMinute)}
            </p>
          )}
        </div>
      </div>

      {/* OK / Abbrechen — sticky Footer; Auswahl ist bereits übernommen, OK schließt nur */}
      <div className="flex gap-2 px-3 pb-3 pt-2 border-t border-gray-100 bg-white shrink-0">
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={handleCancel}
          type="button"
          data-testid="date-time-picker-cancel"
        >
          Schließen
        </Button>
        <Button
          size="sm"
          className="flex-1 bg-[#B91C1C] hover:bg-[#991B1B] text-white"
          onClick={handleOk}
          disabled={!draftDate}
          type="button"
          data-testid="date-time-picker-ok"
        >
          Übernehmen
        </Button>
      </div>
    </div>
  );

  const trigger = (
    <button
      type="button"
      data-testid="date-time-picker-trigger"
      className={cn(
        'w-full text-sm px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] text-left flex items-center gap-2',
        !displayLabel && 'text-gray-400',
        className
      )}
    >
      <CalendarIcon className="w-4 h-4 shrink-0 text-gray-400" />
      <span className="flex-1">{displayLabel ?? placeholder}</span>
      {value && (
        <span
          role="button"
          aria-label="Datum löschen"
          data-testid="date-time-picker-clear"
          onClick={(e) => {
            e.stopPropagation();
            onChange(null);
          }}
          className="text-gray-400 hover:text-gray-600"
        >
          <X className="w-3.5 h-3.5" />
        </span>
      )}
    </button>
  );

  if (isMobile) {
    return (
      <>
        <div onClick={() => setOpen(true)}>{trigger}</div>
        <BottomSheet
          open={open}
          onClose={() => setOpen(false)}
          title="Datum & Uhrzeit"
          height="auto"
        >
          {pickerContent}
        </BottomSheet>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        className="z-[100] w-auto p-0 shadow-lg overflow-hidden flex flex-col"
        style={{ maxHeight: 'var(--radix-popover-content-available-height, 85vh)' }}
        align="start"
        side="bottom"
        sideOffset={6}
        collisionPadding={12}
        avoidCollisions
      >
        {pickerContent}
      </PopoverContent>
    </Popover>
  );
}
